/* ==========================================================================
   VITAORA — theme behaviour
   Vanilla ES2018. No dependencies. Everything is namespaced under VT.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.VITAORA || {};
  var routes = CFG.routes || {};
  var strings = CFG.strings || {};

  /* ------------------------------------------------------------- helpers -- */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts); }

  function formatMoney(cents) {
    var format = CFG.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    // Trim a trailing .00 so $99.00 reads as $99, matching the design.
    if (value.indexOf('.00') === value.length - 3) value = value.slice(0, -3);
    return format
      .replace(/\{\{\s*amount(_no_decimals)?(_with_comma_separator)?\s*\}\}/g, function () {
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      })
      .replace(/<[^>]*>/g, '');
  }

  var toastTimer = null;
  function toast(message) {
    var el = $('#VtToast');
    var text = $('#VtToastText');
    if (!el || !text) return;
    text.textContent = message;
    el.hidden = false;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3200);
  }

  /* ---------------------------------------------------- focus management -- */

  var lastFocus = null;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function openOverlay(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var first = $(FOCUSABLE, el);
    if (first) first.focus();
    el.dataset.trap = 'on';
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    document.body.style.overflow = '';
    el.dataset.trap = 'off';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Tab') return;
    var open = $('.vt-overlay.is-open, .vt-modal.is-open');
    if (!open) return;
    if (e.key === 'Escape') { closeOverlay(open); return; }
    var items = $$(FOCUSABLE, open).filter(function (n) { return n.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-vt-open]');
    if (opener) {
      var target = $('#' + opener.getAttribute('data-vt-open'));
      if (target) { e.preventDefault(); openOverlay(target); }
      return;
    }
    var closer = e.target.closest('[data-vt-close]');
    if (closer) {
      e.preventDefault();
      closeOverlay(closer.closest('.vt-overlay, .vt-modal'));
      return;
    }
    if (e.target.classList && e.target.classList.contains('vt-overlay__scrim')) {
      closeOverlay(e.target.closest('.vt-overlay, .vt-modal'));
    }
  });

  /* -------------------------------------------------------------- header -- */

  function initHeader() {
    var header = $('[data-vt-header]');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var searchToggle = $('[data-vt-search-toggle]');
    var searchBar = $('[data-vt-search-bar]');
    on(searchToggle, 'click', function () {
      var open = searchBar.hidden;
      searchBar.hidden = !open;
      searchToggle.setAttribute('aria-expanded', String(open));
      if (open) { var input = $('input', searchBar); if (input) input.focus(); }
    });
  }

  /* ------------------------------------------------------- scroll reveal -- */

  function initReveal() {
    var nodes = $$('.vt-reveal');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var delay = parseInt(entry.target.getAttribute('data-vt-delay') || '0', 10);
        setTimeout(function () { entry.target.classList.add('is-visible'); }, delay);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ----------------------------------------------------------------- FAQ -- */

  function initFaq() {
    $$('[data-vt-faq]').forEach(function (list) {
      $$('[data-vt-faq-trigger]', list).forEach(function (trigger) {
        on(trigger, 'click', function () {
          var expanded = trigger.getAttribute('aria-expanded') === 'true';
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          // One open at a time keeps the section short and scannable.
          $$('[data-vt-faq-trigger]', list).forEach(function (other) {
            other.setAttribute('aria-expanded', 'false');
            var p = document.getElementById(other.getAttribute('aria-controls'));
            if (p) p.classList.remove('is-open');
          });
          if (!expanded) {
            trigger.setAttribute('aria-expanded', 'true');
            if (panel) panel.classList.add('is-open');
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------- gallery -- */

  function initGallery() {
    var gallery = $('[data-vt-gallery]');
    if (!gallery) return;
    var frame = $('[data-vt-gallery-frame]', gallery);
    var items = $$('[data-vt-gallery-item]', gallery);
    var thumbs = $$('[data-vt-thumb]', gallery);
    var badge = $('[data-vt-gallery-badge]', gallery);

    function show(index) {
      items.forEach(function (item, i) { item.classList.toggle('is-active', i === index); });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (badge && items[index]) badge.textContent = items[index].getAttribute('data-vt-caption') || '';
    }

    thumbs.forEach(function (thumb, i) { on(thumb, 'click', function () { show(i); }); });

    if (frame && window.matchMedia('(hover: hover)').matches) {
      on(frame, 'mouseenter', function () { frame.classList.add('is-zoomed'); });
      on(frame, 'mouseleave', function () {
        frame.classList.remove('is-zoomed');
        var img = $('.vt-gallery__item.is-active img', frame);
        if (img) img.style.transformOrigin = '50% 50%';
      });
      on(frame, 'mousemove', function (e) {
        var img = $('.vt-gallery__item.is-active img', frame);
        if (!img) return;
        var r = frame.getBoundingClientRect();
        img.style.transformOrigin =
          ((e.clientX - r.left) / r.width) * 100 + '% ' + ((e.clientY - r.top) / r.height) * 100 + '%';
      });
    }

    // Jump the gallery to the image tied to the chosen variant, when there is one.
    document.addEventListener('vt:variant:change', function (e) {
      var mediaId = e.detail && e.detail.variant && e.detail.variant.featured_media_id;
      if (!mediaId) return;
      for (var i = 0; i < items.length; i++) {
        if (items[i].getAttribute('data-vt-media-id') === String(mediaId)) { show(i); return; }
      }
    });
  }

  /* -------------------------------------------------- variants / packs -- */

  function initProductForm() {
    $$('[data-vt-product]').forEach(function (root) {
      var dataEl = $('[data-vt-variants]', root);
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = $('[data-vt-variant-id]', root);
      var priceEl = $('[data-vt-price]', root);
      var compareEl = $('[data-vt-compare]', root);
      var saveEl = $('[data-vt-save]', root);
      var perUnitEl = $('[data-vt-perunit]', root);
      var submit = $('[data-vt-submit]', root);
      var submitLabel = submit ? $('.vt-btn__label', submit) : null;
      var packNote = $('[data-vt-pack-note]', root);
      var sellingPlanInput = $('[data-vt-selling-plan]', root);
      var onetimeBtn = $('[data-vt-purchase="onetime"]', root);
      var subBtn = $('[data-vt-purchase="subscription"]', root);
      var subSelect = $('[data-vt-selling-plan-select]', root);
      var subPriceEl = $('[data-vt-sub-price]', root);
      var otPriceEl = $('[data-vt-onetime-price]', root);

      var current = variants.filter(function (v) { return String(v.id) === String(idInput.value); })[0] || variants[0];
      var subscribed = false;

      function sellingPlanPrice(variant, planId) {
        if (!planId) return variant.price;
        var allocations = variant.selling_plan_allocations || [];
        for (var i = 0; i < allocations.length; i++) {
          if (String(allocations[i].selling_plan_id) === String(planId)) return allocations[i].price;
        }
        return variant.price;
      }

      function activePrice() {
        return subscribed ? sellingPlanPrice(current, subSelect && subSelect.value) : current.price;
      }

      function render() {
        var price = activePrice();
        var compare = current.compare_at_price || 0;

        if (priceEl) priceEl.textContent = formatMoney(price);

        if (compareEl) {
          var showCompare = compare > price;
          compareEl.hidden = !showCompare;
          if (showCompare) compareEl.textContent = formatMoney(compare);
        }

        if (saveEl) {
          var saved = compare > price ? compare - price : 0;
          saveEl.hidden = saved <= 0;
          if (saved > 0) {
            saveEl.textContent = (strings.save || 'Save') + ' ' + formatMoney(saved) +
              (subscribed ? ' ' + (saveEl.getAttribute('data-vt-sub-suffix') || '') : '');
          }
        }

        if (perUnitEl) {
          var units = parseInt(current.vt_units || '0', 10);
          var servings = parseInt(perUnitEl.getAttribute('data-vt-servings') || '0', 10);
          if (units > 0 && servings > 0) {
            perUnitEl.textContent = formatMoney(Math.round(price / (units * servings))) + ' / ' + (perUnitEl.getAttribute('data-vt-unit-label') || 'serving');
            perUnitEl.hidden = false;
          } else {
            perUnitEl.hidden = true;
          }
        }

        if (otPriceEl) otPriceEl.textContent = formatMoney(current.price);
        if (subPriceEl && subSelect) subPriceEl.textContent = formatMoney(sellingPlanPrice(current, subSelect.value));

        if (packNote) packNote.textContent = current.title;

        if (submit) {
          submit.disabled = !current.available;
          if (submitLabel) {
            submitLabel.textContent = current.available
              ? (strings.addToCart || 'Add to cart') + ' — ' + formatMoney(price)
              : (strings.soldOut || 'Sold out');
          }
        }

        // Keep the sticky mobile bar in sync.
        var barPrice = $('[data-vt-buybar-price]');
        var barTitle = $('[data-vt-buybar-variant]');
        if (barPrice) barPrice.textContent = formatMoney(price);
        if (barTitle) barTitle.textContent = current.title;

        document.dispatchEvent(new CustomEvent('vt:variant:change', { detail: { variant: current } }));
      }

      function select(variantId) {
        var found = variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
        if (!found) return;
        current = found;
        idInput.value = found.id;
        $$('[data-vt-pack]', root).forEach(function (label) {
          var active = label.getAttribute('data-vt-pack') === String(found.id);
          label.classList.toggle('is-active', active);
          var input = $('input', label);
          if (input) input.checked = active;
        });
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', found.id);
          history.replaceState({}, '', url.toString());
        }
        render();
      }

      $$('[data-vt-pack]', root).forEach(function (label) {
        var input = $('input', label);
        on(input, 'change', function () { select(label.getAttribute('data-vt-pack')); });
      });

      function setSubscribed(value) {
        subscribed = value;
        if (onetimeBtn) onetimeBtn.classList.toggle('is-active', !value);
        if (subBtn) subBtn.classList.toggle('is-active', value);
        if (onetimeBtn) onetimeBtn.setAttribute('aria-pressed', String(!value));
        if (subBtn) subBtn.setAttribute('aria-pressed', String(value));
        if (subSelect) subSelect.disabled = !value;
        if (sellingPlanInput) sellingPlanInput.value = value && subSelect ? subSelect.value : '';
        render();
      }

      on(onetimeBtn, 'click', function () { setSubscribed(false); });
      on(subBtn, 'click', function () { setSubscribed(true); });
      on(subSelect, 'change', function () {
        if (sellingPlanInput) sellingPlanInput.value = subSelect.value;
        render();
      });

      // AJAX add to cart
      var form = $('[data-vt-product-form]', root);
      on(form, 'submit', function (e) {
        if (CFG.cartType !== 'drawer') return; // let Shopify handle a normal POST
        e.preventDefault();
        if (submit) { submit.classList.add('is-loading'); submit.disabled = true; }
        fetch(routes.cart_add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [buildLine(form)] })
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.status) throw new Error(data.description || data.message);
            toast((strings.added || 'Added to your cart'));
            return refreshCart(true);
          })
          .catch(function (err) { toast(err.message || 'Something went wrong. Please try again.'); })
          .then(function () {
            if (submit) { submit.classList.remove('is-loading'); submit.disabled = !current.available; }
          });
      });

      function buildLine(f) {
        var line = { id: idInput.value, quantity: 1 };
        var qtyField = $('[name="quantity"]', f);
        if (qtyField) line.quantity = parseInt(qtyField.value, 10) || 1;
        if (sellingPlanInput && sellingPlanInput.value) line.selling_plan = sellingPlanInput.value;
        return line;
      }

      // Sticky mobile bar reuses the same form.
      var barBtn = $('[data-vt-buybar-submit]');
      on(barBtn, 'click', function () {
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });

      render();
    });
  }

  /* ----------------------------------------------------------- buy bar -- */

  function initBuyBar() {
    var bar = $('[data-vt-buybar]');
    if (!bar) return;
    var onScroll = function () { bar.classList.toggle('is-visible', window.scrollY > 1000); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --------------------------------------------------------------- cart -- */

  function refreshCart(open) {
    return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderCart(cart);
        if (open) {
          var drawer = $('#VtCartDrawer');
          if (drawer) openOverlay(drawer);
        }
        return cart;
      });
  }

  function renderCart(cart) {
    $$('[data-vt-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    var drawer = $('#VtCartDrawer');
    if (!drawer) return;

    var empty = $('[data-vt-cart-empty]', drawer);
    var body = $('[data-vt-cart-body]', drawer);
    var itemsEl = $('[data-vt-cart-items]', drawer);
    var subtotalEl = $('[data-vt-cart-subtotal]', drawer);
    var savingsEl = $('[data-vt-cart-savings]', drawer);
    var titleCount = $('[data-vt-cart-title-count]', drawer);

    if (titleCount) titleCount.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';

    if (!cart.item_count) {
      if (empty) empty.hidden = false;
      if (body) body.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    if (body) body.hidden = false;

    if (itemsEl) {
      itemsEl.innerHTML = cart.items.map(function (item, index) {
        var img = item.image
          ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="" loading="lazy" width="80" height="80">'
          : '';
        var variantLine = [];
        if (item.variant_title && item.variant_title !== 'Default Title') variantLine.push(item.variant_title);
        if (item.selling_plan_allocation) variantLine.push(item.selling_plan_allocation.selling_plan.name);
        return '' +
          '<li class="vt-cart__item" data-vt-line="' + (index + 1) + '">' +
            '<div class="vt-cart__thumb">' + img + '</div>' +
            '<div class="vt-cart__body">' +
              '<div class="vt-cart__row">' +
                '<div>' +
                  '<a class="vt-cart__name" href="' + item.url + '">' + item.product_title + '</a>' +
                  (variantLine.length ? '<p class="vt-cart__variant">' + variantLine.join(' · ') + '</p>' : '') +
                '</div>' +
                '<span class="vt-cart__linetotal">' + formatMoney(item.final_line_price) + '</span>' +
              '</div>' +
              '<div class="vt-cart__controls">' +
                '<div class="vt-qty">' +
                  '<button type="button" data-vt-qty="-1" aria-label="Decrease quantity of ' + item.product_title + '">' + icon('minus') + '</button>' +
                  '<span class="vt-qty__value">' + item.quantity + '</span>' +
                  '<button type="button" data-vt-qty="1" aria-label="Increase quantity of ' + item.product_title + '">' + icon('plus') + '</button>' +
                '</div>' +
                '<button type="button" class="vt-cart__remove" data-vt-remove>Remove</button>' +
              '</div>' +
            '</div>' +
          '</li>';
      }).join('');
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

    if (savingsEl) {
      // cart.js line items expose original vs final line price, which reflects
      // real applied discounts. compare_at_price is NOT available here.
      var saved = cart.items.reduce(function (sum, item) {
        return sum + Math.max(0, item.original_line_price - item.final_line_price);
      }, 0);
      savingsEl.hidden = saved <= 0;
      if (saved > 0) savingsEl.textContent = 'You save ' + formatMoney(saved);
    }

    var meter = $('[data-vt-ship-meter]', drawer);
    if (meter) {
      var threshold = CFG.freeShippingThreshold || 0;
      if (threshold <= 0) {
        meter.hidden = true;
      } else {
        meter.hidden = false;
        var remaining = Math.max(0, threshold - cart.items_subtotal_price);
        var pct = Math.min(100, (cart.items_subtotal_price / threshold) * 100);
        $('[data-vt-ship-text]', meter).textContent = remaining > 0
          ? formatMoney(remaining) + ' ' + (strings.freeShippingRemaining || 'away from free shipping')
          : (strings.freeShippingUnlocked || 'Free shipping unlocked');
        $('[data-vt-ship-fill]', meter).style.width = pct + '%';
      }
    }
  }

  function icon(name) {
    var paths = {
      minus: '<line x1="5" y1="12" x2="19" y2="12"/>',
      plus: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>'
    };
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' + paths[name] + '</svg>';
  }

  function changeLine(line, quantity) {
    return fetch(routes.cart_change, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(renderCart);
  }

  function initCart() {
    var drawer = $('#VtCartDrawer');
    if (!drawer) return;

    on(drawer, 'click', function (e) {
      var row = e.target.closest('[data-vt-line]');
      if (!row) return;
      var line = parseInt(row.getAttribute('data-vt-line'), 10);
      var qtyBtn = e.target.closest('[data-vt-qty]');
      if (qtyBtn) {
        var delta = parseInt(qtyBtn.getAttribute('data-vt-qty'), 10);
        var currentQty = parseInt($('.vt-qty__value', row).textContent, 10);
        changeLine(line, Math.max(0, currentQty + delta));
        return;
      }
      if (e.target.closest('[data-vt-remove]')) changeLine(line, 0);
    });

    refreshCart(false);
  }

  /* ------------------------------------------------------------ carousel -- */

  function initCarousel() {
    $$('[data-vt-rail]').forEach(function (rail) {
      var wrap = rail.closest('[data-vt-carousel]') || document;
      $$('[data-vt-rail-nav]', wrap).forEach(function (btn) {
        on(btn, 'click', function () {
          var dir = parseInt(btn.getAttribute('data-vt-rail-nav'), 10);
          rail.scrollBy({ left: dir * rail.clientWidth * 0.85, behavior: 'smooth' });
        });
      });
    });
  }

  /* ---------------------------------------------------------- newsletter -- */

  function initNewsletter() {
    $$('[data-vt-newsletter]').forEach(function (form) {
      if (form.tagName === 'FORM') return; // a real Shopify form posts normally
    });
  }

  /* ----------------------------------------------------------------- go -- */

  function init() {
    initHeader();
    initReveal();
    initFaq();
    initGallery();
    initProductForm();
    initBuyBar();
    initCart();
    initCarousel();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor support: re-bind when a section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
